// Unpublished Work © 2025-2026 Deere & Company.
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Box, DatePicker, MultiSelect} from '@deere/isg.component-library';
import {generateTimeList, getDateTime} from 'Utils/time-utils';
import moment from 'moment';

const DEFAULT_WIDTH = 200;

export function DateTimePicker(props) {
    const {
        dateProps: {
            validatedDate, hideDatePicker = false, ...otherDateProps
        },
        timeProps: {
            timeFormat,
            timeIntervals,
            timeLabel = '',
            validatedTime = '',
            width = DEFAULT_WIDTH,
            ...otherTimeProps
        },
        allowEmpty = false,
        translations,
        onChange,
        gap
    } = props;

    const [isTimeDropdownOpen, setIsTimeDropdownOpen] = React.useState(false);

    const computeInitialDate = React.useCallback(() => {
        if (!validatedDate) {
            return allowEmpty ? null : new Date();
        }

        if (moment.isMoment(validatedDate)) {
            return validatedDate.toDate();
        }

        if (validatedDate instanceof Date) {
            return validatedDate;
        }

        const date = new Date(validatedDate);

        if (isNaN(date.getTime())) {
            return allowEmpty ? null : new Date();
        }

        return date;
    }, [validatedDate, allowEmpty]);

    const initialDate = React.useMemo(() => computeInitialDate(), [computeInitialDate]);

    const [selectedDate, setSelectedDate] = React.useState(initialDate);
    const [selectedTime, setSelectedTime] = React.useState(validatedTime);

    React.useEffect(() => {
        if ((selectedDate?.getTime?.() || null) !== (initialDate?.getTime?.() || null)) {
            setSelectedDate(initialDate);
        }
    }, [initialDate]);

    React.useEffect(() => {
        if (validatedTime !== selectedTime) {
            setSelectedTime(validatedTime);
        }
    }, [validatedTime]);

    const times = React.useMemo(() => generateTimeList(timeIntervals, timeFormat), [timeIntervals, timeFormat]);

    React.useEffect(() => {
        if (!isTimeDropdownOpen || !selectedTime) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            const selectedItem = document.querySelector(`[data-testid="selectable-list-item-${selectedTime}"]`);

            selectedItem?.scrollIntoView({
                block: 'start'
            });
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [isTimeDropdownOpen, selectedTime]);

    const handleDateChange = React.useCallback(
        ({
            date = selectedDate, time = selectedTime
        } = {}) => {
            const dateTime = getDateTime(date, time);

            onChange(dateTime);
        },
        [onChange, selectedDate, selectedTime]
    );

    const dateValue = selectedDate || (allowEmpty ? null : new Date());

    const datePickerHelperText = () => {
        if (allowEmpty || validatedDate) {
            return '';
        }
        return translations.INVALIDATED_DATE;
    };

    return (
        <>
            <Box
                className='date-time-picker'
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: gap || '16px',
                    '& > .MuiBox-root:first-of-type': hideDatePicker && {
                        width: '100%'
                    }

                }}
            >
                {!hideDatePicker && <DatePicker
                    helperText={datePickerHelperText()}
                    label={translations.DATE}
                    onChange={(date) => {
                        setSelectedDate(date);
                        handleDateChange({
                            date
                        });
                    }}
                    value={dateValue}
                    {...otherDateProps}
                />}
                <MultiSelect
                    disableSorting={true}
                    items={times}
                    label={timeLabel !== '' ? timeLabel : translations.TIME}
                    labels={{
                        multiSelect: 'MultiSelect',
                        placeholder: 'Select Time',
                        emptyList: 'No Items'
                    }}
                    onChange={([time]) => {
                        setSelectedTime(time);
                        handleDateChange({
                            time
                        });
                    }}
                    onChangeOpen={setIsTimeDropdownOpen}
                    open={isTimeDropdownOpen}
                    selectedIds={[selectedTime]}
                    single
                    width={width}
                    {...otherTimeProps}
                />
            </Box>
        </>
    );
}

DateTimePicker.propTypes = {
    allowEmpty: PropTypes.bool,
    dateProps: PropTypes.object,
    gap: PropTypes.string,
    onChange: PropTypes.func,
    timeProps: PropTypes.object,
    translations: PropTypes.translations
};

export default DateTimePicker;
